import { useState, useEffect, useCallback } from 'react';
import axios, { AxiosResponse } from 'axios';
import apiClient, { ApiErrorResponse, LoadingState } from './api';
import type { Dataset, DatasetSearchRequest, RepositoryStats, DownloadJob, DownloadRequest } from './types';

/**
 * Build an ApiErrorResponse from a caught error, narrowing axios errors
 * to extract the backend's `detail` field where available.
 */
function toApiError(err: unknown, fallbackMessage: string): ApiErrorResponse {
  if (axios.isAxiosError<ApiErrorResponse>(err)) {
    return {
      message: err.message || fallbackMessage,
      status: err.response?.status,
      detail: err.response?.data?.detail,
    };
  }
  return { message: err instanceof Error ? err.message : fallbackMessage };
}

/**
 * Hook options for useApi
 */
export interface UseApiOptions<T> {
  /**
   * Automatically fetch data on mount
   */
  auto?: boolean;
  /**
   * Callback when data is loaded successfully
   */
  onSuccess?: (data: T) => void;
  /**
   * Callback when an error occurs
   */
  onError?: (error: ApiErrorResponse) => void;
}

/**
 * Return type for useApi hook
 */
export interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: ApiErrorResponse | null;
  loadingState: LoadingState;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for API calls with loading and error states
 *
 * @example
 * const { data, loading, error, refetch } = useApi<User>(() =>
 *   apiClient.get('/api/user')
 * );
 */
export function useApi<T = unknown>(
  apiCall: () => Promise<AxiosResponse<T>>,
  options?: UseApiOptions<T>
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiErrorResponse | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');

  const loading =
    loadingState === 'loading' || loadingState === 'idle';

  /**
   * Execute the API call
   */
  const execute = useCallback(async () => {
    setError(null);
    try {
      const response = await apiCall();
      setData(response.data);
      options?.onSuccess?.(response.data);
    } catch (err: unknown) {
      // Enhanced error handling with specific messages
      let message = 'An error occurred';
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const detail = axios.isAxiosError<ApiErrorResponse>(err) ? err.response?.data?.detail : undefined;
      const code = axios.isAxiosError(err) ? err.code : undefined;
      const rawMessage = err instanceof Error ? err.message : undefined;

      if (status === 429) {
        message = 'Too many requests. Please wait a moment and try again.';
      } else if (status === 502 || status === 503) {
        message = 'The service is temporarily unavailable. Please try again in a few moments.';
      } else if (status === 504) {
        message = 'The request timed out. Please check your connection and try again.';
      } else if (code === 'ECONNABORTED' || rawMessage?.includes('timeout')) {
        message = 'The request timed out. Please check your connection and try again.';
      } else if (rawMessage === 'Network Error' || !status) {
        message = 'Network connection error. Please check your internet connection.';
      } else if (typeof detail === 'string') {
        message = detail;
      } else if (rawMessage) {
        message = rawMessage;
      }

      const errorData: ApiErrorResponse = {
        message,
        status,
        detail,
      };
      setError(errorData);
      options?.onError?.(errorData);
    }
  }, [apiCall, options]);

  /**
   * Subscribe to loading state changes
   */
  useEffect(() => {
    const handleLoadingStateChange = (state: LoadingState) => {
      setLoadingState(state);
    };

    apiClient.onLoadingStateChange(handleLoadingStateChange);

    return () => {
      apiClient.offLoadingStateChange(handleLoadingStateChange);
    };
  }, []);

  /**
   * Subscribe to error notifications
   */
  useEffect(() => {
    const handleError = (errorData: ApiErrorResponse) => {
      setError(errorData);
      options?.onError?.(errorData);
    };

    apiClient.onError(handleError);

    return () => {
      apiClient.offError(handleError);
    };
  }, [options]);

  /**
   * Fetch data on mount if auto is true
   */
  useEffect(() => {
    if (options?.auto !== false) {
      execute();
    }
  }, [execute, options?.auto]);

  return {
    data,
    loading,
    error,
    loadingState,
    refetch: execute,
  };
}

/**
 * Hook for handling file uploads with progress tracking
 */
export interface UseUploadOptions {
  onSuccess?: () => void;
  onError?: (error: ApiErrorResponse) => void;
}

export interface UseUploadResult {
  upload: (file: File, url: string, additionalData?: Record<string, string | Blob>) => Promise<void>;
  uploading: boolean;
  progress: number;
  error: ApiErrorResponse | null;
}

export function useUpload(options?: UseUploadOptions): UseUploadResult {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<ApiErrorResponse | null>(null);

  const upload = useCallback(
    async (
      file: File,
      url: string,
      additionalData?: Record<string, string | Blob>
    ) => {
      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        await apiClient.uploadFile(url, file, (progress) => {
          setProgress(progress);
        }, additionalData);
        setProgress(100);
        options?.onSuccess?.();
      } catch (err: unknown) {
        const errorData = toApiError(err, 'Upload failed');
        setError(errorData);
        options?.onError?.(errorData);
      } finally {
        setUploading(false);
      }
    },
    [options]
  );

  return {
    upload,
    uploading,
    progress,
    error,
  };
}

/**
 * Hook for fetching and managing public datasets
 */
export interface UsePublicDatasetsOptions {
  organism?: string;
  limit?: number;
  offset?: number;
  data_sources?: string[];
  auto?: boolean;
}

export interface UsePublicDatasetsResult {
  datasets: Dataset[];
  loading: boolean;
  error: ApiErrorResponse | null;
  search: (params: Partial<DatasetSearchRequest>) => Promise<void>;
  refetch: () => Promise<void>;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

export function usePublicDatasets(
  options: UsePublicDatasetsOptions = {}
): UsePublicDatasetsResult {
  const {
    organism = 'human',
    limit = 100,
    offset: initialOffset = 0,
    data_sources,
    auto = true,
  } = options;

  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiErrorResponse | null>(null);
  const [currentOffset, setCurrentOffset] = useState(initialOffset);
  const [hasMore, setHasMore] = useState(true);

  /**
   * Fetch datasets
   */
  const fetchDatasets = useCallback(
    async (append: boolean = false) => {
      setLoading(true);
      setError(null);

      try {
        const { default: endpoints } = await import('./endpoints');
        const response = await endpoints.repositories.listDatasets(
          organism,
          limit,
          append ? currentOffset : 0,
          data_sources
        );

        const newDatasets = response.data || [];
        
        if (append) {
          setDatasets((prev) => [...prev, ...newDatasets]);
        } else {
          setDatasets(newDatasets);
          setCurrentOffset(0);
        }

        // Check if there are more results
        setHasMore(newDatasets.length === limit);
        
        if (append) {
          setCurrentOffset((prev) => prev + limit);
        }
      } catch (err: unknown) {
        setError(toApiError(err, 'Failed to fetch datasets'));
      } finally {
        setLoading(false);
      }
    },
    [organism, limit, currentOffset, data_sources]
  );

  /**
   * Search datasets with filters
   */
  const search = useCallback(async (params: Partial<DatasetSearchRequest>) => {
    setLoading(true);
    setError(null);
    setCurrentOffset(0);

    try {
      const { default: endpoints } = await import('./endpoints');
      const response = await endpoints.repositories.searchDatasets({
        organism: params.organism || organism,
        project_id: params.project_id,
        tissue: params.tissue,
        disease: params.disease,
        study: params.study,
        limit: params.limit || limit,
      });

      setDatasets(response.data || []);
      setHasMore(false); // Search doesn't support pagination
    } catch (err: unknown) {
      setError(toApiError(err, 'Search failed'));
    } finally {
      setLoading(false);
    }
  }, [organism, limit]);

  /**
   * Load more datasets (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!loading && hasMore) {
      await fetchDatasets(true);
    }
  }, [loading, hasMore, fetchDatasets]);

  /**
   * Refetch from beginning
   */
  const refetch = useCallback(async () => {
    setCurrentOffset(0);
    await fetchDatasets(false);
  }, [fetchDatasets]);

  /**
   * Auto-fetch on mount
   */
  useEffect(() => {
    if (auto) {
      fetchDatasets(false);
    }
    // fetchDatasets intentionally excluded: it's recreated whenever currentOffset
    // changes (pagination), which would re-trigger this mount/filter-change effect
    // and reset the list back to page 0 on every loadMore(). Only re-fetch when
    // organism or data_sources actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, organism, data_sources]);

  return {
    datasets,
    loading,
    error,
    search,
    refetch,
    hasMore,
    loadMore,
  };
}

/**
 * Hook for fetching repository statistics
 */
export interface UseRepositoryStatsResult {
  stats: RepositoryStats | null;
  loading: boolean;
  error: ApiErrorResponse | null;
  refetch: () => Promise<void>;
}

export function useRepositoryStats(auto: boolean = true): UseRepositoryStatsResult {
  const [stats, setStats] = useState<RepositoryStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiErrorResponse | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { default: endpoints } = await import('./endpoints');
      const response = await endpoints.repositories.getStats();
      setStats(response.data);
    } catch (err: unknown) {
      setError(toApiError(err, 'Failed to fetch repository stats'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auto) {
      fetchStats();
    }
  }, [auto, fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

/**
 * Hook for managing dataset downloads
 */
export interface UseDownloadManagerOptions {
  pollInterval?: number;
  onComplete?: (job: DownloadJob) => void;
  onError?: (job: DownloadJob) => void;
}

export interface UseDownloadManagerResult {
  downloadJobs: Map<string, DownloadJob>;
  startDownload: (request: DownloadRequest) => Promise<string | null>;
  getJobStatus: (jobId: string) => DownloadJob | null;
  isDownloading: (jobId: string) => boolean;
  isCompleted: (jobId: string) => boolean;
  isFailed: (jobId: string) => boolean;
}

export function useDownloadManager(
  options: UseDownloadManagerOptions = {}
): UseDownloadManagerResult {
  const { pollInterval = 2000, onComplete, onError } = options;

  const [downloadJobs, setDownloadJobs] = useState<Map<string, DownloadJob>>(new Map());
  const [pollingJobIds, setPollingJobIds] = useState<Set<string>>(new Set());

  /**
   * Start a dataset download
   */
  const startDownload = useCallback(
    async (request: DownloadRequest): Promise<string | null> => {
      try {
        const { default: endpoints } = await import('./endpoints');
        const response = await endpoints.repositories.downloadDataset(request);
        const job = response.data;

        setDownloadJobs((prev) => new Map(prev).set(job.job_id, job));

        // Start polling if not completed
        if (job.status === 'pending' || job.status === 'downloading' || job.status === 'processing') {
          setPollingJobIds((prev) => new Set(prev).add(job.job_id));
        }

        return job.job_id;
      } catch (err: unknown) {
        console.error('Failed to start download:', err);
        return null;
      }
    },
    []
  );

  /**
   * Get job status
   */
  const getJobStatus = useCallback(
    (jobId: string) => {
      return downloadJobs.get(jobId) || null;
    },
    [downloadJobs]
  );

  /**
   * Check if downloading
   */
  const isDownloading = useCallback(
    (jobId: string) => {
      const job = downloadJobs.get(jobId);
      return job?.status === 'downloading' || job?.status === 'pending' || job?.status === 'processing';
    },
    [downloadJobs]
  );

  /**
   * Check if completed
   */
  const isCompleted = useCallback(
    (jobId: string) => {
      const job = downloadJobs.get(jobId);
      return job?.status === 'completed';
    },
    [downloadJobs]
  );

  /**
   * Check if failed
   */
  const isFailed = useCallback(
    (jobId: string) => {
      const job = downloadJobs.get(jobId);
      return job?.status === 'failed';
    },
    [downloadJobs]
  );

  /**
   * Poll for job status updates
   */
  useEffect(() => {
    if (pollingJobIds.size === 0) return;

    const interval = setInterval(async () => {
      const { default: endpoints } = await import('./endpoints');
      
      for (const jobId of pollingJobIds) {
        try {
          const response = await endpoints.repositories.getDownloadStatus(jobId);
          const job = response.data;

          setDownloadJobs((prev) => new Map(prev).set(jobId, job));

          // Stop polling if job is complete, failed, or cancelled
          if (
            job.status === 'completed' ||
            job.status === 'failed' ||
            job.status === 'cancelled'
          ) {
            setPollingJobIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(jobId);
              return newSet;
            });

            if (job.status === 'completed') {
              onComplete?.(job);
            } else if (job.status === 'failed') {
              onError?.(job);
            }
          }
        } catch (err) {
          console.error(`Failed to poll job ${jobId}:`, err);
        }
      }
    }, pollInterval);

    return () => clearInterval(interval);
  }, [pollingJobIds, pollInterval, onComplete, onError]);

  return {
    downloadJobs,
    startDownload,
    getJobStatus,
    isDownloading,
    isCompleted,
    isFailed,
  };
}

export default useApi;
